<div class="deposit-fee-catalog p-3">

  <!-- Page Header -->
  <div class="row mb-4">
    <div class="col-md-6">
      <h4>
        <nb-icon icon="archive-outline" class="me-2"></nb-icon>
        Deposit Fee Catalog
      </h4>
      <p class="text-muted">Manage global recyclable deposit fee types used across all stores.</p>
    </div>
    <div class="col-md-6 d-flex justify-content-end align-items-center gap-2">
      <button
        nbButton
        status="warning"
        size="small"
        (click)="confirmAndPush()"
        [disabled]="pushing"
        *ngIf="!showForm"
        title="Sync global default amounts to all stores">
        <nb-icon icon="cloud-upload-outline"></nb-icon>
        Push to all stores
      </button>
      <button
        class="btn btn-primary"
        (click)="openAddForm()"
        *ngIf="!showForm">
        <nb-icon icon="plus-outline" class="me-1"></nb-icon>
        Add Fee Type
      </button>
    </div>
  </div>

  <!-- Create / Edit Form -->
  <nb-card *ngIf="showForm" class="mb-4">
    <nb-card-header>
      <h6 class="mb-0">{{ editingId ? 'Edit Fee Type' : 'New Fee Type' }}</h6>
    </nb-card-header>
    <nb-card-body>
      <form [formGroup]="feeTypeForm">
        <div class="row">
          <!-- Name -->
          <div class="col-md-4 mb-3">
            <label class="form-label">Name *</label>
            <input type="text" class="form-control" formControlName="name" placeholder="e.g. Small plastic bottle">
            <div *ngIf="feeTypeForm.get('name')?.invalid && feeTypeForm.get('name')?.touched" class="text-danger small mt-1">
              Name is required (min 2 characters)
            </div>
          </div>

          <!-- Default Amount -->
          <div class="col-md-3 mb-3">
            <label class="form-label">Default Amount *</label>
            <div class="input-group">
              <input type="number" class="form-control" formControlName="defaultAmount" step="0.01" min="0" placeholder="1.50">
              <span class="input-group-text">DKK</span>
            </div>
            <div *ngIf="feeTypeForm.get('defaultAmount')?.invalid && feeTypeForm.get('defaultAmount')?.touched" class="text-danger small mt-1">
              Default amount is required
            </div>
          </div>

          <!-- Sort Order -->
          <div class="col-md-2 mb-3">
            <label class="form-label">Sort Order</label>
            <input type="number" class="form-control" formControlName="sortOrder" min="0" placeholder="0">
          </div>

          <!-- Active -->
          <div class="col-md-3 mb-3 d-flex align-items-center pt-3">
            <div class="form-check mt-2">
              <input type="checkbox" class="form-check-input" formControlName="active" id="feeTypeActive">
              <label class="form-check-label" for="feeTypeActive"><strong>Active</strong></label>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="mb-3">
          <label class="form-label">Description</label>
          <input type="text" class="form-control" formControlName="description" placeholder="Optional description (e.g. PET bottle under 1L)">
        </div>

        <!-- Provider Code -->
        <div class="mb-3">
          <label class="form-label">Provider code</label>
          <input type="text" class="form-control" formControlName="providerCode" placeholder="e.g. 7000, 7024">
          <small class="form-text text-muted">Optional provider reference code used for auto-matching on CSV import</small>
        </div>

        <!-- EAN Range -->
        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="form-label">EAN Range Start</label>
            <input type="text" class="form-control" formControlName="eanRangeStart" placeholder="e.g. 5000000000000">
            <small class="form-text text-muted">First EAN in the range for auto-assignment</small>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">EAN Range End</label>
            <input type="text" class="form-control" formControlName="eanRangeEnd" placeholder="e.g. 5999999999999">
            <small class="form-text text-muted">Last EAN in the range</small>
          </div>
          <div class="col-md-4 d-flex align-items-center">
            <div class="alert alert-danger p-2 small mb-0 w-100" *ngIf="feeTypeForm.errors?.['eanRangeIncomplete']">
              <nb-icon icon="alert-triangle-outline" class="me-1"></nb-icon>
              Both EAN Range Start and End must be filled, or both must be empty.
            </div>
            <div class="alert alert-info p-2 small mb-0 w-100" *ngIf="!feeTypeForm.errors?.['eanRangeIncomplete'] && (feeTypeForm.get('eanRangeStart')?.value && feeTypeForm.get('eanRangeEnd')?.value)">
              <nb-icon icon="info-outline" class="me-1"></nb-icon>
              Products scanned with an EAN in this range will be automatically assigned this fee type when "Auto-assign by EAN" is enabled on the store.
            </div>
          </div>
        </div>
      </form>
    </nb-card-body>
    <nb-card-footer class="d-flex gap-2 justify-content-end">
      <button class="btn btn-outline-secondary" (click)="cancelForm()">Cancel</button>
      <button class="btn btn-success" (click)="saveForm()" [disabled]="saving">
        <nb-icon *ngIf="saving" icon="loader-outline" class="me-1"></nb-icon>
        {{ saving ? 'Saving...' : (editingId ? 'Update' : 'Create') }}
      </button>
    </nb-card-footer>
  </nb-card>

  <!-- Fee Types Table -->
  <nb-card>
    <nb-card-header>
      <h6 class="mb-0">Global Fee Types ({{ feeTypes.length }})</h6>
    </nb-card-header>
    <nb-card-body>

      <div *ngIf="loading" class="text-center py-4">
        <ngx-spinner bdColor="rgba(0,0,0,0.05)" size="medium" color="#3366ff" type="ball-scale-multiple"></ngx-spinner>
        <p class="mt-3 text-muted">Loading...</p>
      </div>

      <div *ngIf="!loading && feeTypes.length === 0" class="text-center py-4 text-muted">
        <nb-icon icon="archive-outline" style="font-size: 2rem;"></nb-icon>
        <p class="mt-2">No fee types defined yet. Click "Add Fee Type" to get started.</p>
      </div>

      <div *ngIf="!loading && feeTypes.length > 0" class="table-responsive">
        <table class="table table-hover align-middle">
          <thead class="table-light">
              <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Default Amount</th>
              <th>EAN Range</th>
              <th>Provider code</th>
              <th>Sort</th>
              <th>Status</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let ft of feeTypes" [class.table-secondary]="!ft.active" [class.opacity-75]="!ft.active">
              <td>
                <strong>{{ ft.name }}</strong>
              </td>
              <td>
                <span class="text-muted small">{{ ft.description || '—' }}</span>
              </td>
              <td>
                <strong>{{ ft.defaultAmount | number:'1.2-2' }} DKK</strong>
              </td>
              <td>
                <code class="small">{{ eanRangeDisplay(ft) }}</code>
              </td>
              <td>
                <code class="small">{{ ft.providerCode || '—' }}</code>
              </td>
              <td>{{ ft.sortOrder }}</td>
              <td>
                <span [class]="ft.active ? 'badge bg-success' : 'badge bg-secondary'">
                  {{ ft.active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="text-end">
                <button class="btn btn-sm btn-outline-primary me-1" (click)="openEditForm(ft)" title="Edit">
                  <nb-icon icon="edit-outline"></nb-icon>
                </button>
                <button
                  *ngIf="ft.active"
                  class="btn btn-sm btn-outline-danger"
                  (click)="confirmDelete(ft)"
                  title="Deactivate">
                  <nb-icon icon="close-circle-outline"></nb-icon>
                </button>
                <button
                  *ngIf="!ft.active"
                  class="btn btn-sm btn-outline-success"
                  (click)="restoreFeeType(ft)"
                  title="Restore">
                  <nb-icon icon="refresh-outline"></nb-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </nb-card-body>
  </nb-card>

</div>
